import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IconMenu2, IconX, IconCar, IconShield, IconClock, IconMapPin,
  IconStarFilled, IconUsers, IconCalendarEvent, IconBuildingSkyscraper,
  IconBrandInstagram, IconBrandFacebook, IconPhone,
  IconArrowRight, IconCheck
} from '@tabler/icons-react';

// Utility function
const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

// Navigation Component
const Navbar = ({ className }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        'fixed top-0 left-0 right-0 z-50 w-full py-4 px-6 lg:px-10',
        className
      )}
    >
      <div className="max-w-7xl mx-auto">
        <div className="backdrop-blur-xl bg-black/40 border border-[#aea36d]/20 rounded-full px-6 py-3 shadow-[0_0_30px_rgba(174,163,109,0.15)]">
          <div className="flex items-center justify-between">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#aea36d] to-[#8b7a4f] flex items-center justify-center shadow-lg">
                <IconCar className="w-5 h-5 text-black" />
              </div>
              <div>
                <span className="text-[#aea36d] text-xl font-bold tracking-tight">OpenDoorValet</span>
                <p className="text-xs text-white/50">Since 2009</p>
              </div>
            </motion.div>

            <div className="hidden md:flex items-center gap-6">
              <a href="#services" className="text-sm text-white/80 hover:text-[#aea36d] transition-colors">
                Services
              </a>
              <a href="#about" className="text-sm text-white/80 hover:text-[#aea36d] transition-colors">
                About
              </a>
              <a href="#testimonials" className="text-sm text-white/80 hover:text-[#aea36d] transition-colors">
                Testimonials
              </a>
              <a href="#contact" className="text-sm text-white/80 hover:text-[#aea36d] transition-colors">
                Contact
              </a>
              <button className="px-6 py-2 bg-gradient-to-r from-[#aea36d] to-[#8b7a4f] text-black rounded-full font-semibold hover:shadow-[0_0_20px_rgba(174,163,109,0.4)] transition-all">
                Get Quote
              </button>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-[#aea36d] p-2"
              >
                {isMobileMenuOpen ? <IconX size={24} /> : <IconMenu2 size={24} />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden mt-4 pt-4 border-t border-[#aea36d]/20"
              >
                <div className="flex flex-col gap-3">
                  <a href="#services" className="text-white/80 hover:text-[#aea36d] py-2">
                    Services
                  </a>
                  <a href="#about" className="text-white/80 hover:text-[#aea36d] py-2">
                    About
                  </a>
                  <a href="#testimonials" className="text-white/80 hover:text-[#aea36d] py-2">
                    Testimonials
                  </a>
                  <a href="#contact" className="text-white/80 hover:text-[#aea36d] py-2">
                    Contact
                  </a>
                  <button className="px-6 py-2 bg-gradient-to-r from-[#aea36d] to-[#8b7a4f] text-black rounded-full font-semibold mt-2">
                    Get Quote
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.nav>
  );
};

// Animated Background
const GoldGradientBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-black" />

      <motion.div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{
          background: 'radial-gradient(circle, #aea36d 0%, transparent 70%)',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <motion.div
        className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{
          background: 'radial-gradient(circle, #8b7a4f 0%, transparent 70%)',
        }}
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.3, 0.2, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />

      <div className="absolute inset-0" style={{
        backgroundImage: `
          linear-gradient(to right, rgba(174, 163, 109, 0.03) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(174, 163, 109, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
      }} />
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, value, label, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.6 }}
      className="backdrop-blur-xl bg-black/30 border border-[#aea36d]/20 rounded-2xl p-6 hover:border-[#aea36d]/40 transition-all hover:shadow-[0_0_30px_rgba(174,163,109,0.2)]"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#aea36d]/20 to-[#8b7a4f]/20 flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#aea36d]" />
        </div>
        <div>
          <div className="text-3xl font-bold text-white">{value}</div>
          <div className="text-sm text-white/60">{label}</div>
        </div>
      </div>
    </motion.div>
  );
};

// Service Card Component
const ServiceCard = ({ icon: Icon, title, description, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.5 }}
      whileHover={{ scale: 1.05, y: -5 }}
      className="backdrop-blur-xl bg-black/20 border border-[#aea36d]/20 rounded-xl p-6 hover:border-[#aea36d]/40 transition-all hover:shadow-[0_0_20px_rgba(174,163,109,0.15)]"
    >
      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#aea36d] to-[#8b7a4f] flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-black" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-white/60 mb-4">{description}</p>
      <button className="text-[#aea36d] text-sm font-semibold flex items-center gap-2 hover:gap-3 transition-all">
        Learn More <IconArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

// Testimonial Card
const TestimonialCard = ({ name, role, company, quote, rating, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="backdrop-blur-xl bg-black/20 border border-[#aea36d]/20 rounded-xl p-6 hover:border-[#aea36d]/40 transition-all"
    >
      <div className="flex gap-1 mb-4">
        {[...Array(rating)].map((_, i) => (
          <IconStarFilled key={i} className="w-5 h-5 text-[#aea36d]" />
        ))}
      </div>
      <p className="text-white/80 mb-6 italic">"{quote}"</p>
      <div>
        <div className="text-white font-semibold">{name}</div>
        <div className="text-sm text-white/50">{role} • {company}</div>
      </div>
    </motion.div>
  );
};

// Hero Section
const HeroSection = () => {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setEmail('');
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-black">
      <GoldGradientBackground />
      <Navbar />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="inline-flex items-center gap-2 bg-[#aea36d]/10 border border-[#aea36d]/30 rounded-full px-4 py-2 mb-8"
          >
            <IconStarFilled className="w-4 h-4 text-[#aea36d]" />
            <span className="text-sm text-[#aea36d] font-medium">Premium Valet Services Since 2009</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Hospitality Driven
            <span className="block mt-2 bg-gradient-to-r from-[#aea36d] to-[#8b7a4f] bg-clip-text text-transparent">
              Valet Excellence
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="text-xl text-white/70 max-w-2xl mx-auto mb-8"
          >
            Transforming ordinary parking into extraordinary experiences with professionalism, precision, and cutting-edge GSEAT technology.
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto mb-16"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email for a free quote"
              className="flex-1 px-6 py-4 rounded-full bg-white/10 backdrop-blur-sm border border-[#aea36d]/30 text-white placeholder:text-white/50 focus:outline-none focus:border-[#aea36d] transition-all"
              required
            />
            <button
              type="submit"
              className="px-8 py-4 bg-gradient-to-r from-[#aea36d] to-[#8b7a4f] text-black rounded-full font-semibold hover:shadow-[0_0_30px_rgba(174,163,109,0.4)] transition-all whitespace-nowrap"
            >
              Get Instant Quote
            </button>
          </motion.form>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <StatCard icon={IconUsers} value="500+" label="Events Completed" delay={1.1} />
          <StatCard icon={IconClock} value="15+" label="Years Experience" delay={1.2} />
          <StatCard icon={IconMapPin} value="60+" label="Locations Served" delay={1.3} />
          <StatCard icon={IconShield} value="98%" label="Client Satisfaction" delay={1.4} />
        </div>

        {/* Services */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.6 }}
          id="services"
          className="backdrop-blur-xl bg-black/20 border border-[#aea36d]/20 rounded-3xl p-8 lg:p-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
            Our <span className="bg-gradient-to-r from-[#aea36d] to-[#8b7a4f] bg-clip-text text-transparent">Services</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ServiceCard
              icon={IconCalendarEvent}
              title="Event Valet"
              description="Professional valet services for weddings, galas, corporate events, and special occasions."
              delay={1.6}
            />
            <ServiceCard
              icon={IconBuildingSkyscraper}
              title="Commercial Valet"
              description="Premium parking management for hotels, restaurants, and corporate venues with white-glove service."
              delay={1.7}
            />
            <ServiceCard
              icon={IconCar}
              title="Shuttle Services"
              description="Efficient transportation solutions for large gatherings with luxury vehicles and real-time tracking."
              delay={1.8}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Alison C.',
      role: 'Hotel Manager',
      company: 'Luxury Resort NJ',
      quote: 'Always a pleasure! I feel like I am leaving my car in very capable hands!',
      rating: 5
    },
    {
      name: 'Josephina',
      role: 'Event Coordinator',
      company: 'Premier Events',
      quote: 'So quick & easy -- I love this!',
      rating: 5
    },
    {
      name: 'Michael T.',
      role: 'Restaurant Owner',
      company: 'Fine Dining PA',
      quote: 'My valet was very attentive. Professional service that keeps our guests coming back.',
      rating: 5
    }
  ];

  return (
    <section id="testimonials" className="relative py-24 px-6 bg-black overflow-hidden">
      <GoldGradientBackground />

      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Trusted by <span className="bg-gradient-to-r from-[#aea36d] to-[#8b7a4f] bg-clip-text text-transparent">Industry Leaders</span>
          </h2>
          <p className="text-xl text-white/60">Don't just take our word for it</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} {...testimonial} delay={index * 0.2} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer id="contact" className="relative bg-black border-t border-[#aea36d]/20 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-[#aea36d] to-[#8b7a4f] rounded-lg flex items-center justify-center">
                <IconCar className="w-6 h-6 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-[#aea36d]">ODV</h3>
            </div>
            <p className="text-white/60 mb-4">Excellence in hospitality-driven valet services since 2009.</p>
            <div className="flex gap-3">
              <a href="#" className="p-2 bg-white/10 rounded-lg text-[#aea36d] hover:bg-[#aea36d] hover:text-black transition-all">
                <IconBrandFacebook className="w-5 h-5" />
              </a>
              <a href="#" className="p-2 bg-white/10 rounded-lg text-[#aea36d] hover:bg-[#aea36d] hover:text-black transition-all">
                <IconBrandInstagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-white/60">
              <li><a href="#" className="hover:text-[#aea36d] transition-colors">Commercial Valet</a></li>
              <li><a href="#" className="hover:text-[#aea36d] transition-colors">Event Services</a></li>
              <li><a href="#" className="hover:text-[#aea36d] transition-colors">Shuttle Services</a></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-white/60">
              <li><a href="#" className="hover:text-[#aea36d] transition-colors">About Us</a></li>
              <li><a href="#" className="hover:text-[#aea36d] transition-colors">Careers</a></li>
              <li><a href="#" className="hover:text-[#aea36d] transition-colors">Blog</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-white/60">
              <li className="flex items-center gap-2">
                <IconMapPin className="w-5 h-5 text-[#aea36d]" />
                <span>NJ, PA & Beyond</span>
              </li>
              <li className="flex items-center gap-2">
                <IconPhone className="w-5 h-5 text-[#aea36d]" />
                <a href="tel:" className="hover:text-[#aea36d] transition-colors">Get Quote</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-white/50 text-sm">
          <div>© 2009-2025 Open Door Valet. All rights reserved.</div>
          <div className="flex gap-6 mt-4 md:mt-0">
            <a href="#" className="hover:text-[#aea36d] transition-colors">Privacy</a>
            <a href="#" className="hover:text-[#aea36d] transition-colors">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Component
export default function ProLandingPage() {
  return (
    <div className="bg-black overflow-x-hidden">
      <HeroSection />
      <TestimonialsSection />
      <Footer />
    </div>
  );
}
